/**
 * PatientTaskInbox — COA-10 G4
 * ==============================
 * Patient-side operational inbox surfacing rows from active treatment
 * case plans where `default_role = patient`. Provides direct CTAs into
 * existing upload / consent flows; does not redesign those.
 *
 * Data: GET /api/patient/tasks (COA-10).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertOctagon, CheckCircle2, Circle, Clock, ClipboardList, FileText,
  ShieldCheck, RefreshCw, UserCog,
} from 'lucide-react';
import { api } from '../../utils/api';

const PILL = (tone) => {
  const map = {
    rose:    'bg-rose-100 text-rose-700 ring-rose-200',
    amber:   'bg-amber-100 text-amber-700 ring-amber-200',
    emerald: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    slate:   'bg-slate-100 text-slate-600 ring-slate-200',
  };
  return `inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${map[tone] || map.slate}`;
};

const FILTERS_KEYS = [
  { id: 'all',       key: 'patient.notificationsCenter.filterAll' },
  { id: 'pending',   key: 'patient.booking.milestone_pending' },
  { id: 'overdue',   key: 'patient.taskInbox.overdue' },
  { id: 'completed', key: 'patient.journey.completed' },
];

const ACTION_CTA_KEYS = {
  upload_document: { key: 'patient.documents.upload',           icon: FileText,      href: '/#/patient/medical-files' },
  sign_consent:    { key: 'patient.consent.iAgree',             icon: ShieldCheck,   href: '/#/patient/consent/before-after' },
  update_profile:  { key: 'patient.profile.editProfile',        icon: UserCog,       href: '/#/patient/clinical-profile/edit' },
  fulfill_profile_request: { key: 'patient.profile.editProfile', icon: UserCog,      href: '/#/patient/clinical-profile/edit' },
  task:            { key: 'patient.common.viewDetails',         icon: ClipboardList, href: null },
};

export default function PatientTaskInbox() {
  const { t } = useTranslation();
  const FILTERS = FILTERS_KEYS.map(f => ({ id: f.id, label: t(f.key) }));
  const ACTION_CTA = Object.fromEntries(
    Object.entries(ACTION_CTA_KEYS).map(([k, v]) => [k, { label: t(v.key), icon: v.icon, href: v.href }])
  );
  const [items, setItems] = useState([]);
  const [buckets, setBuckets] = useState({ all: 0, pending: 0, overdue: 0, completed: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/patient/tasks');
      if (data?.success) {
        setItems(Array.isArray(data.items) ? data.items : []);
        setBuckets(data.buckets || { all: 0, pending: 0, overdue: 0, completed: 0 });
      }
    } catch {
      setError(t('patient.common.error'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((x) => x.status === filter);
  }, [items, filter]);

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm"
      data-testid="patient-task-inbox"
    >
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-4 w-4 text-slate-500 shrink-0" />
          <div>
            <h2 className="text-sm font-semibold text-slate-900" data-testid="patient-task-title">
              {t('patient.taskInbox.title')}
            </h2>
            <p className="text-[11px] text-slate-500">
              {t('patient.taskInbox.subtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchTasks}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="patient-task-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          {t('patient.common.refresh')}
        </button>
      </header>

      <div className="px-5 py-2 border-b border-slate-100 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`patient-task-filter-${f.id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 transition-colors ${
              filter === f.id
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'bg-white text-slate-700 ring-slate-200 hover:ring-slate-300'
            }`}
          >
            {f.label}
            <span className={filter === f.id ? 'text-white/80' : 'text-slate-500'}>
              {buckets[f.id] ?? 0}
            </span>
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">
          {t('patient.taskInbox.tasksCount', { visible: visible.length, total: items.length })}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {loading && (
          <div className="px-5 py-6 text-center text-[12px] text-slate-500">{t('patient.common.loading')}</div>
        )}
        {!loading && error && (
          <div className="px-5 py-6 text-center text-[12px] text-rose-600" data-testid="patient-task-error">
            {error}
          </div>
        )}
        {!loading && !error && visible.length === 0 && (
          <div className="px-5 py-6 text-center" data-testid="patient-task-empty">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-1.5" />
            <div className="text-[12px] font-medium text-slate-700">
              {t('patient.taskInbox.empty')}
            </div>
          </div>
        )}
        {!loading && !error && visible.map((task) => {
          const cta = ACTION_CTA[task.action_kind] || ACTION_CTA.task;
          const CTAIcon = cta.icon;
          return (
            <div
              key={`${task.case_id}::${task.row_id}`}
              data-testid={`patient-task-${task.row_id}`}
              className="block px-5 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {task.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : task.status === 'overdue' ? (
                    <AlertOctagon className="h-4 w-4 text-rose-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-semibold text-slate-900 truncate">
                      {task.task_name}
                    </span>
                    {task.is_escalated && (
                      <span className={PILL('rose')}>{t('patient.journey.escalated')}</span>
                    )}
                    <span className={PILL(
                      task.status === 'completed' ? 'emerald'
                      : task.status === 'overdue' ? 'rose'
                      : 'amber'
                    )}>
                      {task.status === 'completed' ? t('patient.journey.completed') : task.status === 'overdue' ? t('patient.taskInbox.overdue') : t('patient.booking.milestone_pending')}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="truncate" data-testid={`patient-task-source-clinic-${task.row_id}`}>
                      <span className="text-slate-400">{t('patient.taskInbox.clinicPrefix')} </span>
                      {task.clinic_name || (task.action_kind === 'update_profile' ? t('patient.taskInbox.yourMedicalProfile') : t('patient.taskInbox.unassigned'))}
                    </span>
                    <span className="truncate" data-testid={`patient-task-source-treatment-${task.row_id}`}>
                      <span className="text-slate-400">{t('patient.taskInbox.treatmentPrefix')} </span>
                      {task.treatment_name || '—'}
                    </span>
                    {task.case_id && task.case_id !== '__profile__' && (
                      <span className="truncate font-mono" data-testid={`patient-task-source-case-${task.row_id}`}>
                        <span className="text-slate-400">{t('patient.taskInbox.casePrefix')} </span>
                        {String(task.case_id).slice(0, 8)}
                      </span>
                    )}
                    {task.due_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className={task.status === 'overdue' ? 'text-rose-600 font-semibold' : 'text-slate-600'}>
                          {t('patient.taskInbox.duePrefix', { date: String(task.due_at).slice(0, 10) })}
                        </span>
                      </span>
                    )}
                  </div>
                  {/* PROFILE-REQ-01B — provenance block for provider-originated profile requests */}
                  {task.source === 'profile_request_v1' && (
                    <div
                      className="mt-2 rounded-md border border-sky-100 bg-sky-50/60 px-2.5 py-1.5 text-[11.5px] text-slate-700 space-y-1"
                      data-testid={`patient-task-provenance-${task.row_id}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span data-testid={`patient-task-requestor-${task.row_id}`}>
                          <span className="text-slate-500">{t('patient.taskInbox.requestedByPrefix')} </span>
                          <span className="font-semibold text-slate-800">{task.requestor_name || '—'}</span>
                          {task.requestor_role && (
                            <span className="ml-1 text-slate-500">({task.requestor_role})</span>
                          )}
                        </span>
                      </div>
                      {task.reason && (
                        <div data-testid={`patient-task-reason-${task.row_id}`}>
                          <span className="text-slate-500">{t('patient.taskInbox.reasonPrefix')} </span>
                          <span className="text-slate-700">{task.reason}</span>
                        </div>
                      )}
                      {Array.isArray(task.required_fields) && task.required_fields.length > 0 && (
                        <div
                          className="flex flex-wrap items-center gap-1"
                          data-testid={`patient-task-required-fields-${task.row_id}`}
                        >
                          <span className="text-slate-500">{t('patient.taskInbox.requestedFieldsPrefix')} </span>
                          {task.required_fields.map((f) => (
                            <span
                              key={f}
                              className="px-1.5 py-0.5 rounded bg-white border border-sky-200 text-sky-800 text-[10.5px] font-mono"
                              data-testid={`patient-task-field-${task.row_id}-${f}`}
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      )}
                      {task.request_id && (
                        <div className="text-[10.5px] text-slate-500 font-mono" data-testid={`patient-task-request-id-${task.row_id}`}>
                          {t('patient.taskInbox.requestPrefix')} {task.request_id}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {task.status !== 'completed' && cta.href && (
                  <a
                    href={cta.href}
                    data-testid={`patient-task-cta-${task.row_id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <CTAIcon className="h-3 w-3" />
                    {cta.label}
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
